import React, { useEffect, useMemo, useState } from 'react';
import RetencaoServiceFiles from './RetencaoServiceFiles';
import {
  ComercialProposalRequestLayout,
  LeadDetailGroup,
  LeadDetailRow,
} from '../utils/retencaoMarcacaoDisplay';
import type { ServiceCompletionDisplay } from '../utils/retencaoMarcacaoDisplay';

const LeadFactRows: React.FC<{ rows: LeadDetailRow[] }> = ({ rows }) => {
  if (!rows.length) return null;
  return (
    <dl className="rec-lead-facts">
      {rows.map((row) => (
        <div key={row.key} className="rec-lead-fact">
          <dt>{row.label}</dt>
          <dd>
            {row.href ? (
              <a href={row.href} target="_blank" rel="noopener noreferrer">
                {row.value}
              </a>
            ) : (
              row.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const servicoItemCount = (group: LeadDetailGroup) =>
  group.rows.length + (group.files?.length ?? 0);

const ServicoDetailBody: React.FC<{ group: LeadDetailGroup; showServiceLabel?: boolean }> = ({
  group,
  showServiceLabel,
}) => (
  <>
    {showServiceLabel ? (
      <LeadFactRows rows={[{ key: 'servico', label: 'Serviço', value: group.title }]} />
    ) : null}
    <LeadFactRows rows={group.rows} />
    {group.files && group.files.length > 0 ? (
      <RetencaoServiceFiles files={group.files} leadId={group.leadId ?? null} />
    ) : null}
  </>
);

const ServiceCompletionsBlock: React.FC<{
  completions: ServiceCompletionDisplay[];
  leadId: number | null;
  completedAt: string | null;
  completedBy: string | null;
  completionNotes: string | null;
  isFullyCompleted: boolean;
}> = ({ completions, leadId, completedAt, completedBy, completionNotes, isFullyCompleted }) => {
  const [open, setOpen] = useState(true);

  if (!completions.length) return null;

  return (
    <section className={`rec-request-outcome rec-request-outcome--${isFullyCompleted ? 'done' : 'partial'}`}>
      <button
        type="button"
        className="rec-request-outcome-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="rec-request-outcome-title">Respostas do BackOffice</span>
        <span className="rec-lead-servico-chevron" aria-hidden>
          {open ? '▼' : '▶'}
        </span>
      </button>
      {open && (
        <div className="rec-request-outcome-body">
          {(completedAt || completedBy) && (
            <div className="rec-request-outcome-meta">
              {completedBy && (
                <p>
                  <strong>Concluído por:</strong> {completedBy}
                </p>
              )}
              {completedAt && (
                <p>
                  <strong>Concluído em:</strong> {completedAt}
                </p>
              )}
            </div>
          )}
          {completions.map((sc) => (
            <div
              key={sc.instanceId}
              className={`rec-request-scd rec-request-scd--${sc.tone}`}
            >
              <div className="rec-request-scd-head">
                <h5>{sc.title}</h5>
                <span className={`rec-request-scd-badge rec-request-scd-badge--${sc.tone}`}>
                  {sc.statusLabel}
                </span>
              </div>
              {sc.resolvedByOther && (
                <p className="rec-request-scd-hint">Marcado como resolvido por outro serviço.</p>
              )}
              {(sc.completedBy || sc.completedAt) && (
                <p className="rec-request-scd-meta">
                  {sc.completedBy && <span>Por {sc.completedBy}</span>}
                  {sc.completedAt && <span> · {sc.completedAt}</span>}
                </p>
              )}
              {(sc.cancelledBy || sc.cancelledAt || sc.cancellationNotes) && (
                <div className="rec-request-scd-cancel">
                  {sc.cancelledBy && (
                    <p>
                      <strong>Cancelado por:</strong> {sc.cancelledBy}
                    </p>
                  )}
                  {sc.cancelledAt && (
                    <p>
                      <strong>Cancelado em:</strong> {sc.cancelledAt}
                    </p>
                  )}
                  {sc.cancellationNotes && (
                    <p>
                      <strong>Motivo:</strong> {sc.cancellationNotes}
                    </p>
                  )}
                </div>
              )}
              <LeadFactRows rows={sc.emailDataRows} />
              {sc.files.length > 0 && (
                <RetencaoServiceFiles files={sc.files} leadId={leadId} />
              )}
            </div>
          ))}
          {completionNotes && (
            <div className="rec-request-completion-notes">
              <h5>Notas de conclusão</h5>
              <p>{completionNotes}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const CancellationBlock: React.FC<{
  cancellation: NonNullable<ComercialProposalRequestLayout['cancellation']>;
}> = ({ cancellation }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="rec-request-outcome rec-request-outcome--cancelled">
      <button
        type="button"
        className="rec-request-outcome-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="rec-request-outcome-title">Cancelado</span>
        <span className="rec-lead-servico-chevron" aria-hidden>
          {open ? '▼' : '▶'}
        </span>
      </button>
      {open && (
        <div className="rec-request-outcome-body">
          {cancellation.cancelledBy && (
            <p>
              <strong>Cancelado por:</strong> {cancellation.cancelledBy}
            </p>
          )}
          {cancellation.cancelledAt && (
            <p>
              <strong>Cancelado em:</strong> {cancellation.cancelledAt}
            </p>
          )}
          {cancellation.feedback && (
            <div className="rec-request-cancel-feedback">
              <strong>Motivo do cancelamento</strong>
              <p>{cancellation.feedback}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

type RequestServicosProps = {
  requestId: string;
  servicos: LeadDetailGroup[];
  expandedServicos: Record<string, boolean>;
  onToggleServico: (key: string) => void;
};

const RequestServicosPanel: React.FC<RequestServicosProps> = ({
  requestId,
  servicos,
  expandedServicos,
  onToggleServico,
}) => {
  if (!servicos.length) {
    return <p className="rec-lead-panel-empty">Sem detalhes de serviço neste pedido.</p>;
  }
  const multi = servicos.length > 1;
  return (
    <div className="rec-lead-detail-group rec-lead-servicos-panel">
      <h4 className="rec-lead-detail-group-title">
        {multi ? 'Detalhes dos Serviços' : 'Detalhes do Serviço'}
      </h4>
      <div className="rec-lead-servicos-block">
        {servicos.map((group) => {
          const key = `${requestId}:${group.id}`;
          const isOpen = !!expandedServicos[key];
          return multi ? (
            <section key={key} className={`rec-lead-servico-accordion ${isOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className="rec-lead-servico-trigger"
                onClick={() => onToggleServico(key)}
                aria-expanded={isOpen}
              >
                <span className="rec-lead-servico-chevron" aria-hidden>
                  {isOpen ? '▼' : '▶'}
                </span>
                <span className="rec-lead-servico-trigger-title">{group.title}</span>
                <span className="rec-lead-servico-trigger-meta">
                  {servicoItemCount(group)} {servicoItemCount(group) === 1 ? 'item' : 'itens'}
                </span>
              </button>
              {isOpen && (
                <div className="rec-lead-servico-body">
                  <ServicoDetailBody group={group} />
                </div>
              )}
            </section>
          ) : (
            <ServicoDetailBody key={key} group={group} showServiceLabel />
          );
        })}
      </div>
    </div>
  );
};

type Props = {
  requests: ComercialProposalRequestLayout[];
  expandedServicos: Record<string, boolean>;
  onToggleServico: (key: string) => void;
};

const RetencaoComercialRequests: React.FC<Props> = ({
  requests,
  expandedServicos,
  onToggleServico,
}) => {
  const [expandedRequests, setExpandedRequests] = useState<Record<string, boolean>>({});

  const requestIdsKey = useMemo(
    () => requests.map((r) => r.id).join('|'),
    [requests]
  );

  useEffect(() => {
    setExpandedRequests({});
  }, [requestIdsKey]);

  const sorted = useMemo(() => requests, [requests]);

  if (!sorted.length) {
    return <p className="rec-lead-panel-empty">Não há pedidos de proposta nesta marcação.</p>;
  }

  const toggleRequest = (id: string) => {
    setExpandedRequests((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const formatSubmitted = (iso: string | null) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="rec-comercial-requests">
      {sorted.length > 1 && (
        <p className="rec-comercial-requests-hint">
          {sorted.length} pedidos de proposta nesta marcação — mais recente primeiro.
        </p>
      )}
      {sorted.map((req, index) => {
        const isOpen = !!expandedRequests[req.id];
        const submitted = formatSubmitted(req.submittedAt);
        const splitColumns = !!req.empresa && req.servicos.length > 0;
        return (
          <section
            key={req.id}
            className={`rec-comercial-request ${isOpen ? 'is-open' : ''}`}
          >
            <button
              type="button"
              className="rec-comercial-request-trigger"
              onClick={() => toggleRequest(req.id)}
              aria-expanded={isOpen}
            >
              <span className="rec-lead-servico-chevron" aria-hidden>
                {isOpen ? '▼' : '▶'}
              </span>
              <span className="rec-comercial-request-title">
                {sorted.length > 1 ? (
                  <>
                    Pedido {sorted.length - index}
                    <span className="rec-comercial-request-id">{req.propostaId}</span>
                  </>
                ) : (
                  <span className="rec-comercial-request-id">{req.propostaId}</span>
                )}
              </span>
              <span className={`rec-request-status rec-request-status--${req.status.tone}`}>
                {req.status.label}
              </span>
              {submitted && (
                <span className="rec-comercial-request-date">{submitted}</span>
              )}
            </button>

            {isOpen && (
              <div className="rec-comercial-request-body">
                <div
                  className={`rec-lead-details${splitColumns ? ' rec-lead-details--split' : ''}`}
                >
                  {req.empresa && (
                    <div className="rec-lead-details-col rec-lead-details-col--empresa">
                      <section className="rec-lead-detail-group">
                        <h4 className="rec-lead-detail-group-title">{req.empresa.title}</h4>
                        <LeadFactRows rows={req.empresa.rows} />
                      </section>
                    </div>
                  )}
                  {req.servicos.length > 0 && (
                    <div className="rec-lead-details-col rec-lead-details-col--servicos">
                      <RequestServicosPanel
                        requestId={req.id}
                        servicos={req.servicos}
                        expandedServicos={expandedServicos}
                        onToggleServico={onToggleServico}
                      />
                    </div>
                  )}
                </div>
                {req.showCompletionBlock && (
                  <ServiceCompletionsBlock
                    completions={req.serviceCompletions}
                    leadId={req.leadId}
                    completedAt={req.completedAt}
                    completedBy={req.completedBy}
                    completionNotes={req.completionNotes}
                    isFullyCompleted={req.boardStatus === 'completed'}
                  />
                )}
                {req.cancellation && <CancellationBlock cancellation={req.cancellation} />}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default RetencaoComercialRequests;
